<div class="phone-input-wrapper" [formGroup]="form">
  <!-- Single unified control with country selector and phone input -->
  <div class="relative flex items-center rounded-lg border border-gray-300 bg-white shadow-sm transition-colors focus-within:border-brand-300 focus-within:ring-3 focus-within:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900"
    [class.border-error-500]="isInvalid"
    [class.opacity-60]="disabled()"
    [class.cursor-not-allowed]="disabled()">

    <!-- Country Selector (left side) -->
    <div class="relative flex-shrink-0 border-r border-gray-300 dark:border-gray-700" cdkOverlayOrigin #origin="cdkOverlayOrigin">
      <button
        type="button"
        [disabled]="disabled()"
        (click)="toggleDropdown()"
        (keydown)="onCountryButtonKeyDown($event)"
        [attr.aria-expanded]="dropdownOpen()"
        [attr.aria-haspopup]="'listbox'"
        role="combobox"
        class="flex h-11 items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-800 transition-colors hover:bg-gray-50 focus:outline-hidden dark:text-white/90 dark:hover:bg-gray-800"
        [class.opacity-60]="disabled()"
        [class.cursor-not-allowed]="disabled()">
        @if (selectedCountry(); as country) {
          <img [src]="'assets/flags/' + country.isoCode.toLowerCase() + '.svg'" [alt]="country.name" class="size-5 rounded-full object-cover" />
          <span class="text-sm font-medium">{{ country.dialCode }}</span>
        } @else {
          <span class="text-sm text-gray-400 dark:text-gray-500">País</span>
        }
        <svg
          class="ml-1 size-3.5 text-gray-500 transition-transform duration-200 dark:text-gray-400"
          [style.transform]="dropdownOpen() ? 'rotate(180deg)' : 'rotate(0deg)'"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none">
          <path
            d="M3.5 5.25L7 8.75L10.5 5.25"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
      </button>

      <!-- Dropdown Overlay -->
      <ng-template
        cdkConnectedOverlay
        [cdkConnectedOverlayOrigin]="origin"
        [cdkConnectedOverlayOpen]="dropdownOpen()"
        [cdkConnectedOverlayWidth]="overlayWidth()"
        [cdkConnectedOverlayPositions]="overlayPositions"
        [cdkConnectedOverlayHasBackdrop]="false"
        class="phone-country-dropdown">
        <div
          class="z-50 min-w-[320px] max-h-64 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
          <!-- Search Input -->
          <div class="border-b border-gray-200 p-2 dark:border-gray-700">
            <div class="relative">
              <input
                #searchInput
                type="text"
                [value]="searchTerm()"
                (input)="onSearch($event)"
                (keydown)="onSearchKeyDown($event)"
                placeholder="Buscar país o código..."
                class="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 pl-9 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white/90 dark:placeholder:text-white/30" />
              <svg
                class="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400 dark:text-gray-500"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none">
                <path
                  d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
                <path
                  d="M14 14L11.1 11.1"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </div>
          </div>

          <!-- Country List -->
          <div #countryList class="max-h-52 overflow-y-auto p-1" role="listbox">
            @for (country of filteredCountries(); track country.countryId; let i = $index) {
              <button
                type="button"
                class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                [class.bg-brand-50]="highlightedIndex() === i"
                [class.dark:bg-brand-500/10]="highlightedIndex() === i"
                [attr.aria-selected]="highlightedIndex() === i"
                role="option"
                (click)="selectCountry(country)"
                (keydown)="onCountryItemKeyDown($event, country, i)"
                (mouseenter)="highlightedIndex.set(i)">
                <img [src]="'assets/flags/' + country.isoCode.toLowerCase() + '.svg'" [alt]="country.name" class="size-6 flex-shrink-0 rounded-full object-cover" />
                <div class="flex min-w-0 flex-1 items-center justify-between gap-3">
                  <span class="truncate text-sm font-medium text-gray-800 dark:text-white/90">{{ country.name }}</span>
                  <span class="flex-shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">{{ country.dialCode }}</span>
                </div>
              </button>
            } @empty {
              <p class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No encontramos coincidencias.</p>
            }
          </div>
        </div>
      </ng-template>
    </div>

    <!-- Phone Number Input (right side, integrated) -->
    <div class="flex-1">
      <input
        #phoneInput
        type="text"
        formControlName="phoneNumber"
        [mask]="phoneMask()"
        [attr.placeholder]="phonePlaceholder()"
        (blur)="onBlur()"
        class="h-11 w-full border-0 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-hidden focus:ring-0 dark:text-white/90 dark:placeholder:text-white/30"
        [class.opacity-60]="disabled()"
        [class.cursor-not-allowed]="disabled()" />
    </div>
  </div>

  <!-- Error Messages -->
  @if (isInvalid) {
    <p class="mt-1 text-xs text-error-500">{{ errorMessage }}</p>
  }
</div>
